import { jsonText } from '../utils/sql-dialect.js';
export const PROPOSAL_PROGRAMS = [
    { key: 'govramp', label: 'GovRAMP', rules: [{ batch_prefix: 'govramp-' }, { change_prefix: 'chg-m328-' }, { state_field: 'program_key', prefix: 'govramp' }] },
    {
        key: 'fedramp', label: 'FedRAMP 20x', rules: [
            { batch_prefix: 'fedramp-' }, { batch_prefix: 'ksi-' }, { change_prefix: 'chg-m339-' },
            { state_field: 'to_control_id', prefix: 'fedramp-' }, { state_field: 'framework_id', prefix: 'fedramp-' },
            { state_field: 'program', prefix: 'fedramp-' }, { state_field: 'program_key', prefix: 'fedramp' },
        ],
    },
    {
        key: 'cmmc', label: 'CMMC', rules: [
            { batch_prefix: 'cmmc-' }, { change_prefix: 'chg-m266-' }, { change_prefix: 'chg-m269-' },
            { state_field: 'framework_id', prefix: 'cmmc-' }, { state_field: 'program_key', prefix: 'cmmc' },
        ],
    },
    { key: 'soc2', label: 'SOC 2', rules: [{ batch_prefix: 'soc2-' }, { state_field: 'to_control_id', prefix: 'soc2:' }, { state_field: 'framework_id', prefix: 'soc2' }] },
    { key: 'nist-800-53', label: 'NIST 800-53 catalog', rules: [{ batch_prefix: 'nist-800-53-' }, { state_field: 'framework_id', prefix: 'nist-800-53' }] },
    { key: 'provider', label: 'Cloud provider inheritance', rules: [{ batch_prefix: 'provider-inheritance@' }, { target_types: ['provider_inheritance_catalog', 'provider_inheritance_objective'] }] },
    { key: 'laws', label: 'Laws and regulations', rules: [{ target_types: ['legislation', 'jurisdiction_rule', 'jurisdiction_term', 'case_law', 'breach_event', 'research_source'] }] },
];
export const PROPOSAL_PROGRAM_LABELS = {
    ...Object.fromEntries(PROPOSAL_PROGRAMS.map((p) => [p.key, p.label])),
    other: 'Other',
};
export function isProposalProgramKey(v) {
    return typeof v === 'string' && v in PROPOSAL_PROGRAM_LABELS;
}
function parse(json) {
    if (!json)
        return {};
    try {
        const v = JSON.parse(json);
        return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
    }
    catch {
        return {};
    }
}
const str = (v) => (typeof v === 'string' ? v : '');
function ruleMatches(rule, c, batchId, state) {
    if ('batch_prefix' in rule)
        return batchId.startsWith(rule.batch_prefix);
    if ('change_prefix' in rule)
        return c.change_id.startsWith(rule.change_prefix);
    if ('target_types' in rule)
        return rule.target_types.includes(c.target_type);
    return str(state[rule.state_field]).startsWith(rule.prefix);
}
export function programForProposal(c) {
    const batchId = str(parse(c.impact).batch_id);
    const state = parse(c.proposed_state);
    for (const p of PROPOSAL_PROGRAMS)
        if (p.rules.some((r) => ruleMatches(r, c, batchId, state)))
            return p.key;
    return 'other';
}
/**
 * SQL literal for a rule constant. Rule values come only from
 * PROPOSAL_PROGRAMS (code, never caller input); they are inlined rather than
 * bound because the per-program counts would otherwise exceed D1's
 * 100-bound-parameter limit. Quotes are still escaped.
 */
const lit = (v) => `'${v.replace(/'/g, "''")}'`;
const prefixSql = (expr, prefix) => `substr(${expr}, 1, ${prefix.length}) = ${lit(prefix)}`;
/** SQL for one rule, against the `pc.` alias. */
function ruleSql(rule, db) {
    if ('batch_prefix' in rule)
        return { sql: prefixSql(`COALESCE(${jsonText(db, 'pc.impact', 'batch_id')}, '')`, rule.batch_prefix), params: [] };
    if ('change_prefix' in rule)
        return { sql: prefixSql('pc.change_id', rule.change_prefix), params: [] };
    if ('target_types' in rule)
        return { sql: `pc.target_type IN (${rule.target_types.map(lit).join(', ')})`, params: [] };
    if (!/^[a-z_]+$/.test(rule.state_field))
        throw new Error(`bad state_field ${rule.state_field}`);
    return { sql: prefixSql(`COALESCE(${jsonText(db, 'pc.proposed_state', rule.state_field)}, '')`, rule.prefix), params: [] };
}
function anyRuleSql(p, db) {
    const parts = p.rules.map((r) => ruleSql(r, db));
    return { sql: `(${parts.map((x) => x.sql).join(' OR ')})`, params: parts.flatMap((x) => x.params) };
}
/**
 * WHERE fragment selecting one program with first-match-wins semantics: the
 * program's rules match and no earlier program's do. 'other' = none match.
 * Rule values are constants from PROPOSAL_PROGRAMS, never caller input.
 */
export function proposalProgramSql(key, db = 'sqlite') {
    const idx = key === 'other' ? PROPOSAL_PROGRAMS.length : PROPOSAL_PROGRAMS.findIndex((p) => p.key === key);
    const earlier = PROPOSAL_PROGRAMS.slice(0, idx).map((p) => anyRuleSql(p, db));
    const parts = [...earlier.map((e) => ({ sql: `NOT ${e.sql}`, params: e.params }))];
    if (key !== 'other')
        parts.push(anyRuleSql(PROPOSAL_PROGRAMS[idx], db));
    return { sql: parts.map((p) => p.sql).join(' AND ') || '1=1', params: parts.flatMap((p) => p.params) };
}
// ---------------------------------------------------------------------------
// Plain-language summary of what approving the change does.
// ---------------------------------------------------------------------------
const FRAMEWORK_NAMES = {
    soc2: 'SOC 2', 'soc2-ai': 'SOC 2 AI', 'cmmc-l1': 'CMMC Level 1', 'cmmc-l2': 'CMMC Level 2', 'cmmc-l3': 'CMMC Level 3',
    'nist-800-53': 'NIST 800-53', 'fedramp-cr26': 'FedRAMP 20x', 'iso-42001': 'ISO 42001', 'eu-ai-act': 'EU AI Act',
    gdpr: 'GDPR', 'uk-gdpr': 'UK GDPR', hipaa: 'HIPAA', 'nist-ai-rmf': 'NIST AI RMF',
};
/** `soc2:CC6.1` → `SOC 2 CC6.1`; `nist-800-53:ac-2_obj.e` → `NIST 800-53 ac-2_obj.e`. */
export function humanRef(id) {
    const i = id.indexOf(':');
    if (i < 0)
        return id;
    const fw = id.slice(0, i);
    return FRAMEWORK_NAMES[fw] ? `${FRAMEWORK_NAMES[fw]} ${id.slice(i + 1)}` : id;
}
const clip = (s, n = 140) => (s.length > n ? `${s.slice(0, n).trimEnd()}…` : s);
const EDGE_VERBS = {
    partial: 'partly satisfies', equivalent: 'satisfies', full: 'satisfies', satisfies: 'satisfies',
    references: 'is related to (no credit toward)', superset: 'fully covers', subset: 'covers part of',
};
/** One sentence: what approving this change adds or changes. */
export function describeProposal(c) {
    const s = parse(c.proposed_state);
    const update = c.change_type === 'update';
    const verb = c.change_type === 'deprecate' ? 'Retires' : update ? 'Updates' : 'Adds';
    switch (c.target_type) {
        case 'framework_requirement': {
            const fw = FRAMEWORK_NAMES[str(s.framework_id)] ?? str(s.framework_id);
            return `${verb} ${fw} requirement ${str(s.ref)}${s.title ? `: ${str(s.title)}` : ''}`;
        }
        case 'assessment_objective':
            return `${verb} what an assessor checks for ${humanRef(str(s.requirement_id))} (${str(s.ref)}): ${clip(str(s.statement), 110)}`;
        case 'assessment_test': {
            const meta = parse(JSON.stringify(s.metadata ?? {}));
            if (!update)
                return `Adds evidence test “${str(s.title)}”${meta.what_it_does ? `: ${clip(str(meta.what_it_does), 110)}` : ''}`;
            // V109-7-4: a not-applicable-for-a-provider version says so (it neither adds nor removes a map).
            const na = parse(JSON.stringify(meta.v109_not_applicable ?? {}));
            if (Array.isArray(na.objectives) && na.objectives.length) {
                const providers = (Array.isArray(na.providers) ? na.providers : []).map((p) => (p === 'gcp' ? 'Google Cloud' : str(p))).join(', ');
                return `Marks evidence test “${str(s.title)}” not applicable to ${providers} resources for ${na.objectives.map((o) => humanRef(str(o))).join(', ')}`;
            }
            // V109-7 LM-25 / LM-26: a version that only moves or adds a slice check alias says which.
            const released = parse(JSON.stringify(meta.v109_alias_released ?? {}));
            if (released.check_key)
                return `Hands check ${str(released.check_key)} from evidence test “${str(s.title)}” to ${str(released.to_test)}`;
            const linked = parse(JSON.stringify(meta.v109_slice_alias ?? {}));
            if (linked.check_key)
                return `Links check ${str(linked.check_key)} to evidence test “${str(s.title)}”`;
            const added = [meta.v103_objectives, meta.ksi_objectives, meta.v103_moderate_objectives]
                .flatMap((v) => (Array.isArray(v) ? v : []));
            return `Extends evidence test “${str(s.title)}”${added.length ? ` to ${added.length} more objective${added.length === 1 ? '' : 's'}` : ''}`;
        }
        case 'test_objective_map':
            return `Uses test “${str(s.test_key)}” as ${str(s.role) || 'primary'} evidence for ${humanRef(str(s.objective_id))}`;
        case 'baseline_member':
            return `Puts ${humanRef(str(s.requirement_id))} in the ${str(s.baseline_key)} baseline`;
        case 'control_baseline':
            return `${verb} the ${str(s.title) || str(s.baseline_key)} baseline${s.publisher ? ` (${str(s.publisher)})` : ''}`;
        case 'control_edge':
            return `Records that ${humanRef(str(s.from_control_id))} ${EDGE_VERBS[str(s.relation)] ?? str(s.relation)} ${humanRef(str(s.to_control_id))}`;
        case 'legislation':
            return `${verb} law “${clip(str(s.title), 100)}”${s.jurisdiction ? ` (${str(s.jurisdiction)}${s.status ? `, ${str(s.status)}` : ''})` : ''} in the regulatory register`;
        case 'jurisdiction_rule':
            return `${verb} obligations from ${clip(str(s.statute) || str(s.citation), 100)}${s.jurisdiction ? ` (${str(s.jurisdiction)})` : ''} for AI systems it applies to`;
        case 'jurisdiction_term': {
            // ADR-0077: "Adds Colorado's role word “Developer” (counts as provider; C.R.S. 6-1-1701)".
            const what = str(s.kind) === 'tier' ? 'risk-tier word' : 'role word';
            const canon = str(s.canonical).replace(/_/g, ' ');
            return `${verb} ${str(s.jurisdiction) || 'a jurisdiction'}'s ${what} “${clip(str(s.label) || str(s.term_key), 80)}”` +
                `${canon ? ` (counts as ${canon}${s.rank ? `, rank ${String(s.rank)}` : ''}` : ''}${s.citation ? `; ${clip(str(s.citation), 80)}` : ''}${canon ? ')' : ''}`;
        }
        case 'vocabulary': {
            // V112-5-7 / ADR-0116: "Adds the new duty “Take down within 48 hours”, checked by control takedown_within_48h".
            const what = { duty: 'duty', evidence_kind: 'evidence kind', disclosure_field: 'disclosure field' }[str(s.vocabulary)] ?? 'term';
            // Round 1 #3: the control's title, never its id.
            const by = s.control_title ? `, checked by “${clip(str(s.control_title), 80)}”` : s.control_id ? ', checked by an existing control' : '';
            return `Adds the new ${what} “${clip(str(s.label) || str(s.value), 80)}”${by}`;
        }
        case 'certification_timeframe':
            return `Sets the “${str(s.rule_name)}” clock: ${str(s.force)} ${str(s.timeframe_num)} ${str(s.timeframe_type)}${s.certification_class ? ` (Class ${str(s.certification_class)})` : ''}`;
        case 'program_rule_set':
            return `${verb} the ${str(s.program_key)} program rules (version ${str(s.version)})`;
        // v11.7 / ADR-0111 §8: the legal source record.
        case 'source_document':
            return `${verb} a ${str(s.doc_role).replace(/_/g, ' ') || 'source'} document for ${str(s.source_id) || c.target_id || 'a source'}`;
        case 'jurisdiction_source':
            return `${verb} ${str(s.jurisdiction) || 'a jurisdiction'}'s ${str(s.role).replace(/_/g, ' ') || 'source'} page`;
        case 'legislative_session':
            return `${verb} ${str(s.jurisdiction) || 'a jurisdiction'}'s ${str(s.session_label) || 'legislative session'}${s.adjourns_on ? ` (adjourns ${str(s.adjourns_on)})` : ''}`;
        case 'monitoring_rule_set':
            return `${verb} the ${str(s.family).replace(/_/g, ' ')} monitoring rules (version ${String(s.version ?? '')})`;
        case 'control_guide': {
            // Review #464 M5: citations with no stored source text to ground them are accepted, and said so here.
            const ungrounded = Number(parse(c.impact).ungrounded_citations ?? 0) || (Array.isArray(s.citations) ? s.citations.filter((x) => x?.ungrounded === true).length : 0);
            return `${verb} the guide for ${humanRef(str(s.control_id) || c.target_id || 'a control')}: what done looks like and how to show it${ungrounded ? ` (${ungrounded} citation${ungrounded === 1 ? '' : 's'} not checked against stored source text)` : ''}`;
        }
        case 'provider_inheritance_catalog':
            return `${verb} ${str(s.offering_name)} to the cloud provider catalog`;
        case 'provider_inheritance_objective':
            return `Records who handles ${humanRef(str(s.requirement_id))} on ${str(s.offering_key)}: ${str(s.party)}`;
        case 'cloud_target_mapping':
            return `Maps ${str(s.control_id)} to a cloud policy setting`;
        case 'control_spec':
            return `${verb} the automated check for ${str(s.control_id) || c.target_id || 'a control'}`;
        case 'research_source':
            return s.is_active === 1 || s.is_active === '1' ? 'Turns on a research source' : `${verb} a research source`;
        default:
            return `${verb} ${c.target_type.replace(/_/g, ' ')}${c.target_id ? ` ${c.target_id}` : ''}`;
    }
}
//# sourceMappingURL=proposed-change-program.js.map